import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';

const BrowserSessionContext = createContext();

export const useBrowserSessions = () => useContext(BrowserSessionContext);

const API_BASE_URL = import.meta.env?.VITE_API_BASE_URL || '/api';

export const BrowserSessionProvider = ({ children }) => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const user = useSelector((state) => state.auth?.user);

  // `background` skips the loading flicker — used by the transition poller.
  const loadSessions = useCallback(async (background = false) => {
    try {
      if (!background) setLoading(true);
      const res = await fetch(`${API_BASE_URL}/browser/sessions`, { credentials: 'include' });
      if (res.ok) setSessions(await res.json());
    } catch (err) {
      console.error('Failed to load browser sessions:', err);
    } finally {
      if (!background) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user) loadSessions();
    else { setSessions([]); setLoading(false); }
  }, [user, loadSessions]);

  // Poll while any session is transitioning (starting/stopping) so the UI
  // resolves to running/stopped on its own. Stops polling when none are.
  const anyTransitioning = sessions.some(
    (s) => s.status === 'starting' || s.status === 'stopping'
  );
  useEffect(() => {
    if (!user || !anyTransitioning) return undefined;
    const t = setInterval(() => loadSessions(true), 3000);
    return () => clearInterval(t);
  }, [user, anyTransitioning, loadSessions]);

  /**
   * A 422 from webargs (or from a schema loaded by hand) carries `messages`
   * keyed by location then field, and no `error` — so reading only `error`
   * turned every field-level rejection into the string 'create failed', which
   * is the one thing the backend went out of its way not to send.
   */
  const errorFrom = (body, fallback) => {
    if (body?.error && !body?.messages) return body.error;
    const byField = body?.messages?.json || body?.messages;
    if (byField && typeof byField === 'object') {
      const flat = Object.values(byField).flat().filter(Boolean);
      if (flat.length) return flat.join(' ');
    }
    return body?.error || fallback;
  };

  /**
   * `runtime` decides what a session IS: 'container' pins a residential proxy
   * and boots headed Chrome on our host, 'desktop' is a Chromium profile in
   * the Electron app on this computer. A desktop session sends no proxy — the
   * server rejects one, because Electron applies proxies per-partition and
   * that is not wired up.
   */
  const createSession = async ({ proxy_id, platform, name, persona, runtime = 'container' }) => {
    const body = runtime === 'desktop'
      ? { platform, name, runtime }
      : { proxy_id, platform, name, persona, runtime };
    const res = await fetch(`${API_BASE_URL}/browser/sessions`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      throw new Error(errorFrom(await res.json().catch(() => null), 'create failed'));
    }
    await loadSessions();
    return res.json();
  };

  // Residential proxies selectable for a new session (valid + not already on a
  // live session). Used to populate the create-form proxy dropdown.
  const getAvailableProxies = async () => {
    const res = await fetch(`${API_BASE_URL}/browser/proxies/available`, { credentials: 'include' });
    if (!res.ok) throw new Error('failed to load available proxies');
    return res.json();
  };

  // Re-sync ONLY residential proxies from the provider. Proxies still pinned to
  // a session are soft-invalidated (not deleted) rather than dropped. Returns
  // { sync, affected_sessions } — sessions now stranded on an invalid proxy.
  const refreshResidentialProxies = async () => {
    const res = await fetch(`${API_BASE_URL}/browser/proxies/refresh`, {
      method: 'POST', credentials: 'include',
    });
    await loadSessions();
    if (!res.ok) throw new Error((await res.json()).error || 'refresh failed');
    return res.json();
  };

  // Repoint a session to a new residential proxy. Bounces the container if the
  // session is running (stop -> reassign -> start).
  const reassignProxy = async (id, proxyId) => {
    const res = await fetch(`${API_BASE_URL}/browser/sessions/${id}/reassign-proxy`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ proxy_id: proxyId }),
    });
    await loadSessions();
    if (!res.ok) throw new Error((await res.json()).error || 'reassign failed');
    return res.json();
  };

  const getProxyHistory = async (id) => {
    const res = await fetch(`${API_BASE_URL}/browser/sessions/${id}/proxy-history`, {
      credentials: 'include',
    });
    if (!res.ok) throw new Error('failed to load proxy history');
    return res.json();
  };

  const startSession = async (id) => {
    await fetch(`${API_BASE_URL}/browser/sessions/${id}/start`, { method: 'POST', credentials: 'include' });
    await loadSessions();
  };

  const stopSession = async (id) => {
    await fetch(`${API_BASE_URL}/browser/sessions/${id}/stop`, { method: 'POST', credentials: 'include' });
    await loadSessions();
  };

  const deleteSession = async (id) => {
    await fetch(`${API_BASE_URL}/browser/sessions/${id}`, { method: 'DELETE', credentials: 'include' });
    await loadSessions();
  };

  // Returns { ws_url, token, session_id } for the noVNC client.
  const getLiveView = async (id) => {
    const res = await fetch(`${API_BASE_URL}/browser/sessions/${id}/live`, { credentials: 'include' });
    if (!res.ok) throw new Error('live view unavailable');
    return res.json();
  };

  const takeOver = async (id) => {
    await fetch(`${API_BASE_URL}/browser/sessions/${id}/take-over`, { method: 'POST', credentials: 'include' });
    await loadSessions();
  };

  const release = async (id) => {
    await fetch(`${API_BASE_URL}/browser/sessions/${id}/release`, { method: 'POST', credentials: 'include' });
    await loadSessions();
  };

  const verifyLogin = async (id) => {
    const res = await fetch(`${API_BASE_URL}/browser/sessions/${id}/verify-login`, {
      method: 'POST', credentials: 'include',
    });
    await loadSessions();
    return res.json();
  };

  // conversationId continues an existing multi-turn thread (AI remembers prior
  // turns); pass null/undefined to start a fresh conversation.
  const runAiTask = async (id, instruction, conversationId = null, maxSteps = 40) => {
    const res = await fetch(`${API_BASE_URL}/browser/sessions/${id}/ai-task`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ instruction, max_steps: maxSteps, conversation_id: conversationId }),
    });
    await loadSessions();
    if (!res.ok) throw new Error((await res.json()).error || 'ai task failed');
    return res.json();
  };
  // Type arbitrary text into the focused field of the live Chrome (trusted
  // xdotool) — the reliable replacement for VNC clipboard paste.
  const typeIntoSession = async (id, text, enter = false) => {
    const res = await fetch(`${API_BASE_URL}/browser/sessions/${id}/type`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text, enter }),
    });
    return res.json();
  };
  const getAiConversations = async (id) => {
    const res = await fetch(`${API_BASE_URL}/browser/sessions/${id}/ai-conversations`, { credentials: 'include' });
    if (!res.ok) throw new Error('failed to load conversations');
    return res.json();
  };

  // --- Email domain pool (catch-all domains addresses are minted on) ---
  const getEmailDomains = async () => {
    const res = await fetch(`${API_BASE_URL}/browser/email-domains`, { credentials: 'include' });
    if (!res.ok) throw new Error('failed to load email domains');
    return res.json();
  };
  const createEmailDomain = async (payload) => {
    const res = await fetch(`${API_BASE_URL}/browser/email-domains`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error((await res.json()).error || 'create failed');
    return res.json();
  };
  const updateEmailDomain = async (domainId, payload) => {
    const res = await fetch(`${API_BASE_URL}/browser/email-domains/${domainId}`, {
      method: 'PATCH', credentials: 'include',
      headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error((await res.json()).error || 'update failed');
    return res.json();
  };
  const deleteEmailDomain = async (domainId) => {
    const res = await fetch(`${API_BASE_URL}/browser/email-domains/${domainId}`, {
      method: 'DELETE', credentials: 'include',
    });
    if (!res.ok) throw new Error((await res.json()).error || 'delete failed');
    return res.json();
  };
  const testEmailDomain = async (domainId) => {
    const res = await fetch(`${API_BASE_URL}/browser/email-domains/${domainId}/test`, {
      method: 'POST', credentials: 'include',
    });
    return res.json();
  };

  // --- Per-session email identity + verification-code fetch ---
  const getSessionEmail = async (id) => {
    const res = await fetch(`${API_BASE_URL}/browser/sessions/${id}/email`, { credentials: 'include' });
    if (!res.ok) throw new Error('failed to load session email');
    return res.json();
  };
  // opts: { localpart?, domain_id? } — omit localpart to auto-generate.
  const assignSessionEmail = async (id, opts = {}) => {
    const res = await fetch(`${API_BASE_URL}/browser/sessions/${id}/email`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(opts),
    });
    if (!res.ok) throw new Error((await res.json()).error || 'assign failed');
    return res.json();
  };
  const fetchVerificationCode = async (id, opts = {}) => {
    const res = await fetch(`${API_BASE_URL}/browser/sessions/${id}/fetch-code`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(opts),
    });
    return res.json();
  };
  // Read-only recent inbox for a session's address (debug + non-code mail).
  const getInbox = async (id) => {
    const res = await fetch(`${API_BASE_URL}/browser/sessions/${id}/inbox`, { credentials: 'include' });
    return res.json();
  };

  // --- Phone number pool (SMS verification) ---
  const getPhoneNumbers = async () => {
    const res = await fetch(`${API_BASE_URL}/browser/phone-numbers`, { credentials: 'include' });
    if (!res.ok) throw new Error('failed to load phone numbers');
    return res.json();
  };
  const createPhoneNumber = async (payload) => {
    const res = await fetch(`${API_BASE_URL}/browser/phone-numbers`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error((await res.json()).error || 'create failed');
    return res.json();
  };
  const updatePhoneNumber = async (phoneId, payload) => {
    const res = await fetch(`${API_BASE_URL}/browser/phone-numbers/${phoneId}`, {
      method: 'PATCH', credentials: 'include',
      headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error((await res.json()).error || 'update failed');
    return res.json();
  };
  const deletePhoneNumber = async (phoneId) => {
    const res = await fetch(`${API_BASE_URL}/browser/phone-numbers/${phoneId}`, {
      method: 'DELETE', credentials: 'include',
    });
    if (!res.ok) throw new Error((await res.json()).error || 'delete failed');
    return res.json();
  };
  const getSessionPhone = async (id) => {
    const res = await fetch(`${API_BASE_URL}/browser/sessions/${id}/phone`, { credentials: 'include' });
    if (!res.ok) throw new Error('failed to load session phone');
    return res.json();
  };
  const assignSessionPhone = async (id, opts = {}) => {
    const res = await fetch(`${API_BASE_URL}/browser/sessions/${id}/phone`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(opts),
    });
    if (!res.ok) throw new Error((await res.json()).error || 'assign failed');
    return res.json();
  };
  const fetchSmsCode = async (id) => {
    const res = await fetch(`${API_BASE_URL}/browser/sessions/${id}/fetch-sms`, {
      method: 'POST', credentials: 'include',
    });
    return res.json();
  };

  // --- Session identity / persona notes ---
  const getIdentity = async (id, reveal = false) => {
    const res = await fetch(
      `${API_BASE_URL}/browser/sessions/${id}/identity${reveal ? '?reveal=1' : ''}`,
      { credentials: 'include' }
    );
    if (!res.ok) throw new Error('failed to load identity');
    return res.json();
  };
  const saveIdentity = async (id, payload) => {
    const res = await fetch(`${API_BASE_URL}/browser/sessions/${id}/identity`, {
      method: 'PUT', credentials: 'include',
      headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error((await res.json()).error || 'save failed');
    return res.json();
  };

  // --- Per-account credential vault ---
  const getCredential = async (id, accountId, reveal = false) => {
    const res = await fetch(
      `${API_BASE_URL}/browser/sessions/${id}/accounts/${accountId}/credential${reveal ? '?reveal=1' : ''}`,
      { credentials: 'include' }
    );
    if (!res.ok) throw new Error('failed to load credential');
    return res.json();
  };
  const saveCredential = async (id, accountId, payload) => {
    const res = await fetch(
      `${API_BASE_URL}/browser/sessions/${id}/accounts/${accountId}/credential`,
      {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
      }
    );
    if (!res.ok) throw new Error((await res.json()).error || 'save failed');
    return res.json();
  };
  const getAccountTotp = async (id, accountId) => {
    const res = await fetch(`${API_BASE_URL}/browser/sessions/${id}/accounts/${accountId}/totp`, {
      credentials: 'include',
    });
    return res.json();
  };
  const fillAccountField = async (id, accountId, field) => {
    const res = await fetch(`${API_BASE_URL}/browser/sessions/${id}/accounts/${accountId}/fill`, {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ field }),
    });
    return res.json();
  };

  // Poll the AI drive loop's chat-style event log. `since` = last seen event id.
  const getAiEvents = async (id, since = 0) => {
    const res = await fetch(
      `${API_BASE_URL}/browser/sessions/${id}/ai-events?since=${since}`,
      { credentials: 'include' }
    );
    if (!res.ok) throw new Error('failed to load ai events');
    return res.json();
  };

  const linkAccount = async (id, payload) => {
    const res = await fetch(`${API_BASE_URL}/browser/sessions/${id}/accounts`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    await loadSessions();
    return res.json();
  };

  return (
    <BrowserSessionContext.Provider
      value={{
        sessions, loading, loadSessions,
        getAvailableProxies, refreshResidentialProxies, reassignProxy, getProxyHistory,
        createSession, startSession, stopSession, deleteSession,
        getLiveView, takeOver, release, linkAccount, verifyLogin, runAiTask, getAiEvents,
        getAiConversations, typeIntoSession,
        getEmailDomains, createEmailDomain, updateEmailDomain, deleteEmailDomain, testEmailDomain,
        getSessionEmail, assignSessionEmail, fetchVerificationCode, getInbox,
        getIdentity, saveIdentity,
        getPhoneNumbers, createPhoneNumber, updatePhoneNumber, deletePhoneNumber,
        getSessionPhone, assignSessionPhone, fetchSmsCode,
        getCredential, saveCredential, getAccountTotp, fillAccountField,
      }}
    >
      {children}
    </BrowserSessionContext.Provider>
  );
};
